import { addDays } from 'date-fns';

import { useAvailableDateRangeWithTrialLag } from '../../../filters/hooks/useAvailableDateRangeWithTrialLag';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import { useViewDateGranularity } from '../../../filters/hooks/useViewDateGranularity';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import { END_WEEK_PADDING_DAYS } from '../../DataProvider/postingDiscovery/range-filters';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  formatDataDates,
  formatDateRange,
  getFileName,
  toFileDate,
} from './getFileName';

/**
 * The dates the data covers, as the filter engine resolves the page's date
 * filter — inside a chart, with that chart's own overrides.
 */
const useDataDates = (): string => {
  const { dateFilterId, revelioPageId } = useTabContentConfig();
  const filters = useEffectiveFilters();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const viewGranularity = useViewDateGranularity();
  // The discovery pages list individual postings and people, at day grain,
  // so their dates come from the weekly bounds of the data.
  const bounds = availableDateRange?.week;

  // Posting Discovery lists postings by Post Date: the picked range, with an
  // unpicked side filled from the range the picker itself offers.
  if (revelioPageId === RevelioPageId.POSTING_DISCOVERY) {
    const postDate = filters.find((filter) => filter.id === FilterId.POST_DATE);
    const picked =
      postDate?.type === FilterType.DATE_RANGE ? postDate : undefined;
    const min = picked?.min ?? bounds?.min;
    // A picked end covers its whole week, as the request does, unless the
    // range came in with an exact end.
    const pickedMax =
      picked?.max && !picked.exactEnd
        ? addDays(picked.max, END_WEEK_PADDING_DAYS)
        : picked?.max;
    const max = pickedMax ?? bounds?.max;
    return min && max ? formatDateRange(min, max) : '';
  }
  // Talent Discovery has no date filter: the people are as of the latest week.
  if (revelioPageId === RevelioPageId.TALENT_DISCOVERY) {
    return bounds ? toFileDate(bounds.max) : '';
  }
  // As the date filter's chip shows them.
  return formatDataDates(
    filters.find((filter) => filter.id === dateFilterId),
    viewGranularity
  );
};

/**
 * getFileName with the selected entities and the data dates filled in: the
 * page's download name, or a chart's when chartTitle is given.
 */
export const useFileName = ({
  primaryFilterId,
  chartTitle,
}: {
  primaryFilterId?: PrimaryFilterId;
  chartTitle?: string;
}): string => {
  const { config: pageConfig } = usePageConfig();
  const { primaryFilter } = usePrimaryFilterState(primaryFilterId);
  const dataDates = useDataDates();
  return getFileName({
    pageName: pageConfig.name,
    primaryFilterId,
    chartTitle,
    primarySelections: primaryFilter?.primarySelections,
    dataDates,
  });
};
